import { Card } from 'components/common';
import { CardLoader } from 'components/common/Loading';
import { PageHelmet } from 'components/common/PageHelmet';
import { CalendarHeader } from 'components/views-components/staff/calendar/CalendarHeader';
import { useSiteIntAttributes } from 'hooks/useAttribute';
import { useCallback, useMemo, useState } from 'react';
import {
  STAFF_CLOSE_HOUR_IN_SECONDS,
  STAFF_OPEN_HOUR_IN_SECONDS,
} from '../../configs/siteAndTrustAttributes';
import { useCommonTranslation } from '../../hooks/i18n/useCommonTranslation';
import {
  DateTimeToMinuteOnlyISOString,
  getDateRange,
} from '../../utils/dateUtil';
import { BookAppointmentSlotCard } from './calendar/common/BookAppointmentSlotCard';
import { BookOverflow } from './calendar/overflow/BookOverflow';
import { useStaffCalendar } from './calendar/useStaffCalendar';
import { PatientDetailCard } from './calendar/bookWithPatient/PatientDetailCard';
import { StaffCalendarAppointmentFlowCard } from './calendar/StaffCalendarAppointmentFlowCard';
import { StaffLayout } from './StaffLayout';
import { SangixMainGrid } from './SangixMainGrid';
import { DateRangeType } from '../../types/calendar';
import { CalendarDateRangeView } from '../../components/views-components/staff/calendar/CalendarDateRangeView';
import { SearchPatientList } from '../../components/views-components/staff/search-patient/SearchPatientList';
import { useSearchAndSelectPatient } from '../../components/views-components/staff/search-patient/SearchPatientHooks';

const DEFAULT_DATE_RANGE_TYPE: DateRangeType = 'week';

export const StaffCalendar = () => {
  const { t } = useCommonTranslation();

  const [openHourInSeconds, closeHourInSeconds] = useSiteIntAttributes(
    STAFF_OPEN_HOUR_IN_SECONDS,
    STAFF_CLOSE_HOUR_IN_SECONDS,
  );
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>(
    DEFAULT_DATE_RANGE_TYPE,
  );
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [overflowOpened, setOverflowOpened] = useState(false);

  const dateRange = useMemo(
    () => getDateRange(selectedDate, dateRangeType),
    [selectedDate, dateRangeType],
  );

  const {
    isFetching: isCalendarDataFetching,
    futureAppointmentsSlots,
    refetch,
  } = useStaffCalendar({
    dateRange,
  });
  const [selectedAppointmentSlotTime, setSelectedAppointmentSlotTime] =
    useState<DateTimeToMinuteOnlyISOString>();

  const selectedAppointmentSlot = selectedAppointmentSlotTime
    ? futureAppointmentsSlots?.[selectedAppointmentSlotTime]
    : undefined;

  const isFetching = isCalendarDataFetching;

  const onDateTitleClick = useCallback(
    (date: Date) => {
      const revertedDateRangeType = dateRangeType === 'day' ? 'week' : 'day';
      setSelectedDate(date);
      setDateRangeType(revertedDateRangeType);
    },
    [dateRangeType],
  );

  const {
    isSearchingPatients,
    selectPatient,
    selectedPatient,
    searchPatientFormProps,
    searchPatientListProps,
  } = useSearchAndSelectPatient({
    onPatientSelect: () => {
      setSelectedAppointmentSlotTime(undefined);
    },
  });

  const closeForm = useCallback(
    (keepPatient: boolean) => {
      setSelectedAppointmentSlotTime(undefined);
      if (!keepPatient) {
        selectPatient(undefined);
      }
    },
    [setSelectedAppointmentSlotTime, selectPatient],
  );

  return (
    <StaffLayout>
      <PageHelmet title={t('calendar')} />
      {isSearchingPatients ? (
        <SearchPatientList {...searchPatientListProps} />
      ) : (
        <SangixMainGrid
          main={
            <Card fillHeight>
              <CalendarHeader
                searchPatientFormProps={searchPatientFormProps}
                calendarHeaderUnderLineProps={{
                  selectedDate,
                  onSelectDate: setSelectedDate,
                  onOverflowClicked: () => setOverflowOpened(true),
                  dateRangeType,
                  setDateRangeType,
                }}
              />
              {isFetching ? (
                <CardLoader
                  style={{
                    marginTop: 'var(--s4)',
                  }}
                />
              ) : openHourInSeconds === undefined ? (
                'No Open Hour'
              ) : closeHourInSeconds === undefined ? (
                'No Close Hour'
              ) : futureAppointmentsSlots === undefined ? (
                'No Appointments Data'
              ) : (
                <>
                  <CalendarDateRangeView
                    openHourInSeconds={openHourInSeconds}
                    closeHourInSeconds={closeHourInSeconds}
                    dateRange={dateRange}
                    futureAppointmentsByDate={futureAppointmentsSlots}
                    setSelectedAppointmentsSlot={setSelectedAppointmentSlotTime}
                    selectedAppointmentSlot={selectedAppointmentSlotTime}
                    dateRangeType={dateRangeType}
                    onDateTitleClick={onDateTitleClick}
                    onRefreshClick={refetch}
                  />
                </>
              )}
            </Card>
          }
          secondary={
            <>
              {selectedAppointmentSlot ? (
                <BookAppointmentSlotCard
                  selectedSlotAppointments={selectedAppointmentSlot}
                  selectedPatient={selectedPatient}
                  closeForm={closeForm}
                />
              ) : overflowOpened ? (
                <BookOverflow
                  closeForm={() => {
                    selectPatient(undefined);
                    setOverflowOpened(false);
                  }}
                  selectedPatient={selectedPatient}
                />
              ) : selectedPatient ? (
                <PatientDetailCard
                  key={selectedPatient.id}
                  patient={selectedPatient}
                />
              ) : (
                <StaffCalendarAppointmentFlowCard />
              )}
            </>
          }
        />
      )}
    </StaffLayout>
  );
};
